"use client";

import { AnalyticsSkeleton } from "@/components/ui/skeleton";
import { supabase } from "@/lib/supabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NumberTicker } from "@/components/ui/number-ticker";
import { PageHeader } from "@/components/page-header";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { useCallback, useEffect, useState } from "react";
import type {
  AgentAnalyticsData,
  AgentAnalyticsDetail,
  AgentAnalyticsKind,
  AgentAnalyticsRange as Range,
  AgentUsageRow,
} from "@/types/agent-analytics";

// ─── Constants ─────────────────────────────────────────────────────────────────

const RANGES: { value: Range; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "14d", label: "Last 2 weeks" },
  { value: "30d", label: "Last month" },
  { value: "90d", label: "Last 3 months" },
  { value: "all", label: "All time" },
];

type CreationMetric = "agents" | "managedAgents" | "sessions";

const CREATION_METRICS: { value: CreationMetric; label: string }[] = [
  { value: "agents", label: "Agents" },
  { value: "managedAgents", label: "Managed agents" },
  { value: "sessions", label: "Sessions" },
];

// chart-1/3/5 (not the adjacent 1/2/3) — the token ramp is a lightness
// gradient on one hue, so evenly-spaced steps stay tellable apart; adjacent
// steps read as near-identical at line width.
const creationChartConfig = {
  agents:        { label: "Agents",         color: "var(--chart-1)" },
  managedAgents: { label: "Managed agents", color: "var(--chart-5)" },
  sessions:      { label: "Sessions",        color: "var(--chart-3)" },
} satisfies ChartConfig;

// Same 1/3/5 ramp as the growth chart, for the same reason.
const timelineChartConfig = {
  sessions: { label: "Sessions", color: "var(--chart-1)" },
  people:   { label: "People",   color: "var(--chart-5)" },
  events:   { label: "Events",   color: "var(--chart-3)" },
} satisfies ChartConfig;

const formatDateTime = (iso: string | null) => {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
};

function agentTypeLabel(row: { kind: AgentAnalyticsKind; agentType: string }): string {
  if (row.kind === "managed") return "Managed";
  return row.agentType === "knowledge" ? "Knowledge" : "Custom";
}

/** Query-string tail shared by the overview and drill-down fetches. */
function rangeParams(r: Range, dateRange: DateRange | undefined): string {
  if (r === "custom" && dateRange?.from) {
    const from = dateRange.from.toISOString();
    const to = (dateRange.to ?? dateRange.from).toISOString();
    return `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  }
  return `&range=${r}`;
}

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: number;
  sub?: string;
}) {
  return (
    <Card size="sm" className="gap-3">
      <CardContent className="pt-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="mt-1 text-3xl font-semibold text-foreground tabular-nums">
          <NumberTicker value={value} />
        </p>
        {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

type AgentAnalyticsPanelProps = {
  onBack?: () => void;
};

export function AgentAnalyticsPanel({ onBack }: AgentAnalyticsPanelProps) {
  const [range, setRange] = useState<Range>("30d");
  const [data, setData] = useState<AgentAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Off by default: usage metrics exclude superadmin ("developer") activity
  // unless the admin viewing this page explicitly asks to see it. Creation
  // metrics are never affected by this toggle.
  const [includeDevelopers, setIncludeDevelopers] = useState(false);

  const [creationMetrics, setCreationMetrics] = useState<CreationMetric[]>([
    "agents",
    "managedAgents",
    "sessions",
  ]);

  // Set when a row in the usage rankings table is clicked; swaps the whole
  // panel for that agent's drill-down until the user goes back.
  const [selectedAgent, setSelectedAgent] = useState<AgentUsageRow | null>(null);

  const load = useCallback(
    async (r: Range, dateRange: DateRange | undefined, includeDevs: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const url =
          `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-agent-analytics?includeDevelopers=${includeDevs}` +
          rangeParams(r, dateRange);
        const res = await fetch(url, {
          headers: {
            apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
        });

        const json = await res.json();
        if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load agent analytics.");
        setData(json as AgentAnalyticsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load agent analytics.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (range === "custom") return; // only load custom when the user explicitly applies it
    void load(range, undefined, includeDevelopers);
  }, [load, range, includeDevelopers]);

  const periodLabel =
    range === "custom" && customDateRange?.from
      ? customDateRange.to
        ? `${customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${customDateRange.to.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`
        : customDateRange.from.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : RANGES.find((r) => r.value === range)?.label.toLowerCase() ?? "";

  if (loading && !data) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Agents Analytics" description="Agent creation and usage across the platform" onBack={onBack} />
        <AnalyticsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title="Agents Analytics" description="Agent creation and usage across the platform" onBack={onBack} />
        <div className="flex flex-1 items-center justify-center px-6">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  if (selectedAgent) {
    return (
      <AgentDrillDown
        agent={selectedAgent}
        range={range}
        customDateRange={customDateRange}
        includeDevelopers={includeDevelopers}
        onBack={() => setSelectedAgent(null)}
      />
    );
  }

  const { summary } = data;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader
        title="Agents Analytics"
        description="Agent creation and usage across the platform"
        onBack={onBack}
        actions={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <div className="flex items-center gap-2 pr-1">
              <Switch
                id="include-developers"
                checked={includeDevelopers}
                onCheckedChange={setIncludeDevelopers}
              />
              <label htmlFor="include-developers" className="text-xs text-muted-foreground">
                Include Developers Usage
              </label>
            </div>

            <ToggleGroup
              type="single"
              value={range === "custom" ? "" : range}
              onValueChange={(v) => {
                if (!v) return;
                setCustomDateRange(undefined);
                setRange(v as Range);
              }}
              variant="outline"
              size="sm"
              className="flex-wrap"
            >
              {RANGES.map(({ value, label }) => (
                <ToggleGroupItem key={value} value={value}>
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant={range === "custom" ? "default" : "outline"}
                  size="sm"
                  className="h-8 gap-1.5 px-3 text-xs"
                >
                  <CalendarIcon className="size-3.5" />
                  {range === "custom" && customDateRange?.from ? periodLabel : "Custom"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={customDateRange}
                  onSelect={setCustomDateRange}
                  numberOfMonths={2}
                  disabled={{ after: new Date() }}
                  captionLayout="dropdown"
                />
                <div className="flex justify-end gap-2 border-t border-border px-3 py-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setCustomDateRange(undefined);
                      setCalendarOpen(false);
                      setRange("30d");
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs"
                    disabled={!customDateRange?.from}
                    onClick={() => {
                      setCalendarOpen(false);
                      setRange("custom");
                      void load("custom", customDateRange, includeDevelopers);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="space-y-8 px-6 py-6">

          {/* ── Creation summary ──────────────────────────────────────────── */}
          <div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <KpiCard
                label="Agents"
                value={summary.totalAgents}
                sub={`+${summary.agentsCreated} ${periodLabel}`}
              />
              <KpiCard
                label="Managed agents"
                value={summary.totalManagedAgents}
                sub={`+${summary.managedAgentsCreated} ${periodLabel} · ${summary.activeManagedAgents} active`}
              />
              <KpiCard
                label="Sessions created"
                value={summary.sessionsCreated}
                sub={`${summary.agentSessionsCreated} agent · ${summary.managedSessionsCreated} managed`}
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Creation metrics always include agents and sessions created by developer accounts.
            </p>
          </div>

          {/* ── Usage summary ─────────────────────────────────────────────── */}
          <div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <KpiCard
                label="Agents used"
                value={summary.agentsUsed}
                sub={periodLabel}
              />
              <KpiCard
                label="Sessions used"
                value={summary.sessionsUsed}
                sub={periodLabel}
              />
              <KpiCard
                label="People using agents"
                value={summary.peopleUsingAgents}
                sub={periodLabel}
              />
              <KpiCard
                label="Automated sessions"
                value={summary.automatedSessions}
                sub="cron / email / api / webhook / file"
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {includeDevelopers
                ? `Usage metrics include activity from ${summary.developerCount} developer account${summary.developerCount === 1 ? "" : "s"}.`
                : `Usage metrics exclude ${summary.developerCount} developer account${summary.developerCount === 1 ? "" : "s"} (superadmins). Toggle "Include Developers Usage" to see it.`}
            </p>
          </div>

          {/* ── Growth over time ──────────────────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-2">
              <div>
                <CardTitle>Growth over time</CardTitle>
                <CardDescription>Cumulative totals, continuing from everything created before this window.</CardDescription>
              </div>
              <ToggleGroup
                type="multiple"
                value={creationMetrics}
                onValueChange={(v) => v.length > 0 && setCreationMetrics(v as CreationMetric[])}
                variant="outline"
                size="sm"
                className="flex-wrap"
              >
                {CREATION_METRICS.map(({ value, label }) => (
                  <ToggleGroupItem key={value} value={value} className="text-xs">
                    {label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </CardHeader>
            <CardContent>
              {data.creationTrend.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No data for this period.
                </p>
              ) : (
                <ChartContainer config={creationChartConfig} className="h-[280px] aspect-auto">
                  <LineChart
                    data={data.creationTrend.map((p) => ({
                      label: p.label,
                      agents: p.cumulativeAgents,
                      managedAgents: p.cumulativeManagedAgents,
                      sessions: p.cumulativeSessions,
                    }))}
                    margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                      allowDecimals={false}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    {creationMetrics.map((metric) => (
                      <Line
                        key={metric}
                        dataKey={metric}
                        stroke={`var(--color-${metric})`}
                        strokeWidth={2}
                        dot={false}
                        type="monotone"
                      />
                    ))}
                  </LineChart>
                </ChartContainer>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                {creationMetrics.map((metric) => (
                  <span key={metric} className="flex items-center gap-1.5">
                    <span
                      className="size-2.5 rounded-sm"
                      style={{ background: creationChartConfig[metric].color }}
                    />
                    {creationChartConfig[metric].label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ── Usage rankings ────────────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Usage rankings</CardTitle>
              <CardDescription>
                Every agent and managed agent, including ones with no usage in this window. Click a row for its timeline and people.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={usageColumns}
                data={data.usage}
                getRowKey={(row) => `${row.kind}:${row.id}`}
                onRowClick={setSelectedAgent}
                searchFilter={(row, q) =>
                  row.name.toLowerCase().includes(q) || row.owner.toLowerCase().includes(q)
                }
                searchPlaceholder="Search agents…"
                itemLabel="agent"
                emptyMessage="No agents found."
              />
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}

// ─── Usage rankings table ──────────────────────────────────────────────────────

const usageColumns: DataTableColumn<AgentUsageRow>[] = [
  {
    header: "Agent",
    render: (row) => (
      <div className="min-w-0">
        <p className="truncate font-medium text-foreground">{row.name}</p>
        <div className="mt-0.5 flex items-center gap-1.5">
          <Badge variant="outline" className="text-[10px]">{agentTypeLabel(row)}</Badge>
          {row.archived && <Badge variant="secondary" className="text-[10px]">Archived</Badge>}
        </div>
      </div>
    ),
  },
  { header: "Owner", width: "w-[160px]", render: (row) => <span className="truncate">{row.owner}</span> },
  {
    header: "People",
    width: "w-[90px]",
    align: "right",
    sortKey: "people",
    sortValue: (row) => row.uniquePeople,
    render: (row) => row.uniquePeople,
  },
  {
    header: "Sessions",
    width: "w-[90px]",
    align: "right",
    sortKey: "sessions",
    sortValue: (row) => row.sessionsUsed,
    render: (row) => row.sessionsUsed,
  },
  {
    header: "Events",
    width: "w-[90px]",
    align: "right",
    sortKey: "events",
    sortValue: (row) => row.events,
    render: (row) => row.events,
  },
  {
    header: "Last used",
    width: "w-[160px]",
    sortKey: "lastUsed",
    sortValue: (row) => row.lastUsedAt,
    render: (row) => formatDateTime(row.lastUsedAt),
  },
  {
    header: "Created",
    width: "w-[160px]",
    sortKey: "created",
    sortValue: (row) => row.createdAt,
    render: (row) => formatDateTime(row.createdAt),
  },
];

// ─── Drill-down ─────────────────────────────────────────────────────────────────

type AgentDrillDownProps = {
  agent: AgentUsageRow;
  range: Range;
  customDateRange: DateRange | undefined;
  includeDevelopers: boolean;
  onBack: () => void;
};

const peopleColumns: DataTableColumn<AgentAnalyticsDetail["people"][number]>[] = [
  {
    header: "Person",
    render: (p) => (
      <div className="flex items-center gap-1.5">
        <span className="truncate">{p.name}</span>
        {p.isDeveloper && <Badge variant="secondary" className="text-[10px]">Developer</Badge>}
      </div>
    ),
  },
  {
    header: "Sessions",
    width: "w-[90px]",
    align: "right",
    sortKey: "sessions",
    sortValue: (p) => p.sessions,
    render: (p) => p.sessions,
  },
  {
    header: "Events",
    width: "w-[90px]",
    align: "right",
    sortKey: "events",
    sortValue: (p) => p.events,
    render: (p) => p.events,
  },
  {
    header: "First used",
    width: "w-[170px]",
    sortKey: "firstUsed",
    sortValue: (p) => p.firstUsedAt,
    render: (p) => formatDateTime(p.firstUsedAt),
  },
  {
    header: "Last used",
    width: "w-[170px]",
    sortKey: "lastUsed",
    sortValue: (p) => p.lastUsedAt,
    render: (p) => formatDateTime(p.lastUsedAt),
  },
];

function AgentDrillDown({ agent, range, customDateRange, includeDevelopers, onBack }: AgentDrillDownProps) {
  const [detail, setDetail] = useState<AgentAnalyticsDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const url =
          `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/get-agent-analytics?includeDevelopers=${includeDevelopers}&agentId=${agent.id}&kind=${agent.kind}` +
          rangeParams(range, customDateRange);
        const res = await fetch(url, {
          headers: {
            apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? "",
            Authorization: `Bearer ${session?.access_token ?? ""}`,
          },
        });
        const json = await res.json();
        if (!res.ok || json.error) throw new Error(json.error ?? "Failed to load agent detail.");
        if (!cancelled) setDetail(json as AgentAnalyticsDetail);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Unable to load agent detail.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [agent.id, agent.kind, range, customDateRange, includeDevelopers]);

  const description = `${agentTypeLabel(agent)} · Owned by ${agent.owner}${agent.archived ? " · Archived" : ""}`;

  if (loading && !detail) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title={agent.name} description={description} onBack={onBack} showBack />
        <AnalyticsSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
        <PageHeader title={agent.name} description={description} onBack={onBack} showBack />
        <div className="flex flex-1 items-center justify-center px-6">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-background">
      <PageHeader title={agent.name} description={description} onBack={onBack} showBack />
      <div className="flex-1 overflow-auto">
        <div className="space-y-8 px-6 py-6">

          <div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <KpiCard label="Sessions used" value={detail.sessionsUsed} />
              <KpiCard label="Unique people" value={detail.uniquePeople} />
              <KpiCard label="Events" value={detail.events} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              First used {formatDateTime(detail.firstUsedAt)} · Last used {formatDateTime(detail.lastUsedAt)} (all-time, not clipped to the selected range).
            </p>
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Activity over time</CardTitle>
              <CardDescription>Sessions, people, and events per bucket for this agent.</CardDescription>
            </CardHeader>
            <CardContent>
              {detail.timeline.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No activity in this period.</p>
              ) : (
                <ChartContainer config={timelineChartConfig} className="h-[280px] aspect-auto">
                  <LineChart
                    data={detail.timeline.map((p) => ({
                      label: p.label,
                      sessions: p.sessions,
                      people: p.people,
                      events: p.events,
                    }))}
                    margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                      axisLine={false}
                      tickLine={false}
                      width={36}
                      allowDecimals={false}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    {(["sessions", "people", "events"] as const).map((metric) => (
                      <Line
                        key={metric}
                        dataKey={metric}
                        stroke={`var(--color-${metric})`}
                        strokeWidth={2}
                        dot={false}
                        type="monotone"
                      />
                    ))}
                  </LineChart>
                </ChartContainer>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                {(["sessions", "people", "events"] as const).map((metric) => (
                  <span key={metric} className="flex items-center gap-1.5">
                    <span className="size-2.5 rounded-sm" style={{ background: timelineChartConfig[metric].color }} />
                    {timelineChartConfig[metric].label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle>People</CardTitle>
              <CardDescription>
                {agent.kind === "managed"
                  ? "Everyone recorded in managed_agent_session_message_senders for this agent's sessions."
                  : "Everyone who sent a message to this agent."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={peopleColumns}
                data={detail.people}
                getRowKey={(p) => p.userId}
                searchFilter={(p, q) => p.name.toLowerCase().includes(q)}
                searchPlaceholder="Search people…"
                itemLabel="person"
                emptyMessage={
                  agent.kind === "managed"
                    ? "No individual usage recorded — this agent's sessions may all be automated."
                    : "No usage in this period."
                }
              />
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
}
